import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'
import { useCallback } from 'react'
import { AiBridgeListParams, aiBridgeApi } from '@/api/aiBridgeApi'

export const aiBridgeQueryKeys = {
  all: ['ai-bridges'] as const,
  list: (params?: AiBridgeListParams) =>
    [...aiBridgeQueryKeys.all, 'list', params] as const,
  details: (bridgeId: string) =>
    [...aiBridgeQueryKeys.all, 'details', bridgeId] as const
}

export const useGetAiBridgeList = (
  params: AiBridgeListParams,
  options?: { keepPreviousData?: boolean }
) => {
  return useQuery({
    queryKey: aiBridgeQueryKeys.list(params),
    queryFn: () => aiBridgeApi.getAiBridgeList(params),
    placeholderData: options?.keepPreviousData ? keepPreviousData : undefined
  })
}

export const useGetAiBridgeDetails = (bridgeId: string, enabled?: boolean) => {
  return useQuery({
    queryKey: aiBridgeQueryKeys.details(bridgeId),
    queryFn: () => aiBridgeApi.getAiBridgeDetails(bridgeId),
    enabled
  })
}

export const useCreateAiBridge = () => {
  return useMutation({
    mutationFn: aiBridgeApi.createAiBridge
  })
}

export const useDeleteAiBridge = () => {
  return useMutation({
    mutationFn: aiBridgeApi.deleteAiBridge
  })
}

export const useTestAiBridgePath = () => {
  return useMutation({
    mutationFn: aiBridgeApi.testAiBridgePath
  })
}

export const useTestAiBridge = () => {
  return useMutation({
    mutationFn: aiBridgeApi.testAiBridge
  })
}

export const useRestartAiBridge = () => {
  return useMutation({
    mutationFn: aiBridgeApi.restartAiBridge
  })
}

export const useInvalidateAiBridgeList = () => {
  const queryClient = useQueryClient()

  const invalidateAiBridgeList = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [...aiBridgeQueryKeys.all, 'list']
    })
  }, [queryClient])

  return { invalidateAiBridgeList }
}
